/**
 * Capture on-screen report sections (including charts) as multi-page PDF.
 */
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const DEFAULT_CANVAS_OPTIONS = {
  scale: 2,
  useCORS: true,
  allowTaint: true,
  logging: false,
  backgroundColor: '#ffffff',
};

/** Browser canvas limits — exceeding these breaks toDataURL / jsPDF PNG decode. */
const MAX_CANVAS_EDGE_PX = 8192;
const MAX_CANVAS_AREA_PX = 14_000_000;

function resolveCaptureScale(element, preferredScale = 2) {
  const height = Math.max(element.scrollHeight, element.offsetHeight, 1);
  const width = Math.max(element.scrollWidth, element.offsetWidth, 1);
  let scale = preferredScale;

  while (
    scale > 0.5
    && (height * scale > MAX_CANVAS_EDGE_PX
      || width * scale > MAX_CANVAS_EDGE_PX
      || height * width * scale * scale > MAX_CANVAS_AREA_PX)
  ) {
    scale -= 0.25;
  }

  return scale;
}

function canvasToImage(canvas) {
  try {
    const jpeg = canvas.toDataURL('image/jpeg', 0.84);
    if (jpeg && jpeg.length > 128) {
      return { data: jpeg, format: 'JPEG' };
    }
  } catch {
    // fall through to PNG
  }

  const png = canvas.toDataURL('image/png');
  if (!png || png.length < 128 || png === 'data:,') {
    throw new Error('Could not encode report image. Try again or narrow the scan date range.');
  }

  return { data: png, format: 'PNG' };
}

function addCanvasPagesToPdf(pdf, canvas, margin, pageWidth, pageHeight, startY = margin) {
  const contentWidth = pageWidth - margin * 2;
  const { data, format } = canvasToImage(canvas);
  const imgHeight = (canvas.height * contentWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = 0;
  let y = startY;
  let firstSlice = pageHeight - y - margin;

  while (heightLeft > 0) {
    const sliceHeight = position === 0 ? firstSlice : pageHeight - margin * 2;
    pdf.addImage(data, format, margin, y - position, contentWidth, imgHeight);
    heightLeft -= sliceHeight;
    position += sliceHeight;

    if (heightLeft > 0) {
      pdf.addPage();
      y = margin;
      firstSlice = pageHeight - margin * 2;
    }
  }

  return pdf;
}

export function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function captureElementToCanvas(element, options = {}) {
  if (!element) {
    throw new Error('Nothing to capture on screen. Load the report first.');
  }

  const scale = resolveCaptureScale(element, options.scale ?? DEFAULT_CANVAS_OPTIONS.scale);

  return html2canvas(element, {
    ...DEFAULT_CANVAS_OPTIONS,
    ...options,
    scale,
    scrollY: -window.scrollY,
    windowWidth: Math.max(element.scrollWidth, element.offsetWidth),
    height: element.scrollHeight,
  });
}

/**
 * @param {HTMLElement} element
 * @param {string} filename
 * @param {{ title?: string, subtitle?: string }} [metadata]
 */
export async function exportElementToPdf(element, filename, metadata = {}) {
  const scale = resolveCaptureScale(element, 2);
  const canvas = await captureElementToCanvas(element, { scale });
  const pdf = new jsPDF('p', 'pt', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 24;
  let headerOffset = margin;

  if (metadata.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text(metadata.title, margin, margin + 4);
    headerOffset = margin + 18;
  }

  if (metadata.subtitle) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(90, 90, 90);
    pdf.text(metadata.subtitle, margin, headerOffset);
    pdf.setTextColor(0, 0, 0);
    headerOffset += 14;
  }

  addCanvasPagesToPdf(pdf, canvas, margin, pageWidth, pageHeight, headerOffset);
  pdf.save(filename);
}

/**
 * Capture many small DOM chunks (avoids one giant canvas for long audit reports).
 *
 * @param {HTMLElement} rootElement
 * @param {string} chunkSelector
 */
export async function exportChunksToPdf(rootElement, chunkSelector, filename, metadata = {}) {
  if (!rootElement) {
    throw new Error('Nothing to capture on screen. Load the report first.');
  }

  const chunks = Array.from(rootElement.querySelectorAll(chunkSelector));
  if (chunks.length === 0) {
    throw new Error('Report sections missing. Run the scan again and retry export.');
  }

  const pdf = new jsPDF('p', 'pt', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 24;
  if (metadata.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text(metadata.title, margin, 32);
    if (metadata.subtitle) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(90, 90, 90);
      pdf.text(metadata.subtitle, margin, 48);
      pdf.setTextColor(0, 0, 0);
    }
    pdf.addPage();
  }

  let firstChunk = true;
  for (let index = 0; index < chunks.length; index += 1) {
    const chunk = chunks[index];
    const scale = resolveCaptureScale(chunk, 1.35);
    const canvas = await captureElementToCanvas(chunk, { scale });
    if (!firstChunk) {
      pdf.addPage();
    }
    firstChunk = false;
    addCanvasPagesToPdf(pdf, canvas, margin, pageWidth, pageHeight, margin);
  }

  pdf.save(filename);
}

/**
 * @param {Array<{ element?: HTMLElement|null, title?: string }>} sections
 * @param {string} filename
 * @param {{ title?: string, subtitle?: string }} [metadata]
 */
export async function exportSectionsToPdf(sections, filename, metadata = {}) {
  const validSections = sections.filter((s) => s.element);
  if (!validSections.length) {
    throw new Error('No report sections are visible. Open each tab and run the checks first.');
  }

  const pdf = new jsPDF('p', 'pt', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 24;
  let isFirstPage = true;

  if (metadata.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text(metadata.title, margin, 32);
    if (metadata.subtitle) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(90, 90, 90);
      pdf.text(metadata.subtitle, margin, 48);
      pdf.setTextColor(0, 0, 0);
    }
    pdf.addPage();
    isFirstPage = false;
  }

  for (let index = 0; index < validSections.length; index += 1) {
    const section = validSections[index];
    const canvas = await captureElementToCanvas(section.element, {
      scale: resolveCaptureScale(section.element, 2),
    });

    if (!isFirstPage || index > 0) {
      pdf.addPage();
    }
    isFirstPage = false;

    let y = margin;
    if (section.title) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text(section.title, margin, y + 4);
      y += 20;
    }

    addCanvasPagesToPdf(pdf, canvas, margin, pageWidth, pageHeight, y);
  }

  pdf.save(filename);
}
